/**
 * Updated Alabama Law Weekly Custom Pricing Calculator
 * Now uses AWS Lambda for draft order creation
 * 
 * IMPORTANT: Replace AWS_LAMBDA_ENDPOINT with your actual API Gateway URL
 */

// AWS Lambda endpoint for custom pricing
const AWS_LAMBDA_ENDPOINT = 'https://lgotamgp15.execute-api.us-east-1.amazonaws.com/create-order';

class CustomPricingCalculator {
  constructor() {
    // Product-specific pricing configurations
    this.productConfigs = {
      'alw-sub': {
        name: 'Alabama Law Weekly',
        solePractitioner: 457.54,
        firmBase: 595.35,
        perAttorney: 32.00
      },
      'tam-sub': {
        name: 'Tennessee Attorneys Memo',
        solePractitioner: 463.05,
        firmBase: 711.11,
        perAttorney: 40.00
      }
    };
    
    this.currentProductHandle = this.getCurrentProductHandle();
    this.currentConfig = this.productConfigs[this.currentProductHandle];
    
    this.init();
    
    // Add clear custom pricing functionality
    this.addClearCustomPricingOption();
  }
  
  getCurrentProductHandle() {
    // Get product handle from URL
    const pathParts = window.location.pathname.split('/');
    const productIndex = pathParts.indexOf('products');
    if (productIndex !== -1 && pathParts[productIndex + 1]) {
      return pathParts[productIndex + 1];
    }
    return null;
  }
  
  init() {
    // Only initialize if this product has custom pricing configuration
    if (!this.currentConfig) {
      return;
    }
    
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupEventListeners());
    } else {
      this.setupEventListeners();
    }
  }
  
  setupEventListeners() {
    const solePractitionerRadio = document.getElementById('sole-practitioner');
    const firmRateRadio = document.getElementById('firm-rate');
    const attorneyCountInput = document.getElementById('attorney-count');
    const attorneySection = document.getElementById('attorney-count-section');
    
    if (!solePractitionerRadio || !firmRateRadio) {
      return;
    }
    
    // Set up AWS Lambda integration instead of regular cart
    this.setupLambdaIntegration();
    
    // Rate selection listeners
    solePractitionerRadio.addEventListener('change', () => {
      if (solePractitionerRadio.checked) {
        attorneySection.style.display = 'none';
        this.calculatePrice();
      }
    });
    
    firmRateRadio.addEventListener('change', () => {
      if (firmRateRadio.checked) {
        attorneySection.style.display = 'block';
        this.calculatePrice();
      }
    });
    
    // Attorney count listener
    if (attorneyCountInput) {
      attorneyCountInput.addEventListener('input', () => {
        this.calculatePrice();
      });
    }
    
    // Update pricing labels and main price display
    this.updatePricingLabels();
    this.updateMainPrice(0);
  }
  
  setupLambdaIntegration() {
    // Find and intercept the product form submission - look for the main product form, not installment forms
    const productForm = document.querySelector('product-form form[action*="/cart/add"]') || 
                       document.querySelector('form[data-type="add-to-cart-form"]') ||
                       document.querySelector('.product-form form[action*="/cart/add"]');
    
    if (productForm) {
      productForm.addEventListener('submit', (e) => {
        e.preventDefault();
        this.handleLambdaCheckout();
      });
    }
  }
  
  async handleLambdaCheckout() {
    const currentPricing = this.getCurrentPricing();
    
    // Show loading state
    const submitButton = document.querySelector('button[type="submit"]');
    const originalText = submitButton.textContent;
    submitButton.textContent = 'Creating Order...';
    submitButton.disabled = true;
    
    try {
      const response = await fetch(AWS_LAMBDA_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          productHandle: this.currentProductHandle,
          productName: this.currentConfig.name || this.currentProductHandle,
          rateType: this.getSelectedRateType(),
          attorneyCount: parseInt(document.getElementById('attorney-count')?.value) || 0,
          pricing: {
            solePractitioner: this.currentConfig.solePractitioner,
            firmBase: this.currentConfig.firmBase,
            perAttorney: this.currentConfig.perAttorney
          },
          customerInfo: {
            // Add customer info if available
            email: '', // Could be collected from a form
            firstName: '',
            lastName: ''
          }
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        // Store checkout URL in localStorage for cart redirect handling
        localStorage.setItem('custom_pricing_checkout_url', result.checkoutUrl);
        localStorage.setItem('custom_pricing_active', 'true');
        localStorage.setItem('last_custom_pricing_product', this.currentProductHandle);
        
        // Redirect to Shopify checkout with correct pricing
        window.location.href = result.checkoutUrl;
      } else {
        throw new Error(result.error || 'Failed to create order');
      }
      
    } catch (error) {
      console.error('Error creating custom order:', error);
      alert('Sorry, there was an error creating your order. Please try again or contact support.');
      
      // Restore button state
      submitButton.textContent = originalText;
      submitButton.disabled = false;
    }
  }
  
  getSelectedRateType() {
    const solePractitionerRadio = document.getElementById('sole-practitioner');
    const firmRateRadio = document.getElementById('firm-rate');
    
    if (solePractitionerRadio && solePractitionerRadio.checked) {
      return 'sole_practitioner';
    } else if (firmRateRadio && firmRateRadio.checked) {
      return 'firm_rate';
    }
    return null;
  }
  
  updatePricingLabels() {
    const solePractitionerPriceLabel = document.getElementById('sole-practitioner-price');
    const firmBasePriceLabel = document.getElementById('firm-base-price');
    const perAttorneyPriceLabel = document.getElementById('per-attorney-price');
    
    if (solePractitionerPriceLabel) {
      solePractitionerPriceLabel.textContent = this.formatPrice(this.currentConfig.solePractitioner);
    }
    
    if (firmBasePriceLabel) {
      firmBasePriceLabel.textContent = this.formatPrice(this.currentConfig.firmBase);
    }
    
    if (perAttorneyPriceLabel) {
      perAttorneyPriceLabel.textContent = this.formatPrice(this.currentConfig.perAttorney);
    }
  }
  
  calculatePrice() {
    const solePractitionerRadio = document.getElementById('sole-practitioner');
    const firmRateRadio = document.getElementById('firm-rate');
    const attorneyCountInput = document.getElementById('attorney-count');
    const calculatedPriceElement = document.getElementById('calculated-price');
    
    let totalPrice = 0;
    let rateType = '';
    let attorneyCount = 0;
    
    if (solePractitionerRadio && solePractitionerRadio.checked) {
      totalPrice = this.currentConfig.solePractitioner;
      rateType = 'Sole Practitioner Rate';
    } else if (firmRateRadio && firmRateRadio.checked) {
      attorneyCount = parseInt(attorneyCountInput.value) || 0;
      totalPrice = this.currentConfig.firmBase + (attorneyCount * this.currentConfig.perAttorney);
      rateType = 'Firm Rate';
    }
    
    // Update display
    if (calculatedPriceElement) {
      calculatedPriceElement.textContent = this.formatPrice(totalPrice);
    }
    
    // Update hidden form fields for reference
    this.updateHiddenFields(rateType, attorneyCount, totalPrice);
    
    // Update Shopify's main price display
    this.updateMainPrice(totalPrice);
    
    return totalPrice;
  }
  
  updateHiddenFields(rateType, attorneyCount, totalPrice) {
    const pricingDataField = document.getElementById('pricing-data');
    const attorneyDataField = document.getElementById('attorney-data');
    const finalPriceField = document.getElementById('final-price');
    
    if (pricingDataField) {
      pricingDataField.value = rateType;
    }
    
    if (attorneyDataField) {
      attorneyDataField.value = attorneyCount > 0 ? attorneyCount : '';
    }
    
    if (finalPriceField) {
      finalPriceField.value = this.formatPrice(totalPrice);
    }
  }
  
  updateMainPrice(price) {
    // Update Shopify's main price display elements
    const priceElements = document.querySelectorAll('.price-item, .price__regular .price-item, .price .price-item');
    
    priceElements.forEach(element => {
      if (price > 0) {
        element.textContent = this.formatPrice(price) + ' USD';
      } else {
        element.textContent = '$0.00 USD';
      }
    });
  }
  
  formatPrice(price) {
    return '$' + price.toFixed(2);
  }
  
  // Method to get current pricing for AWS Lambda
  getCurrentPricing() {
    return {
      totalPrice: this.calculatePrice(),
      rateType: document.getElementById('pricing-data')?.value || '',
      attorneyCount: document.getElementById('attorney-data')?.value || '',
      formattedPrice: document.getElementById('final-price')?.value || ''
    };
  }
  
  addClearCustomPricingOption() {
    // Check if there's an active custom pricing session
    const hasActivePricing = localStorage.getItem('custom_pricing_active') === 'true';
    
    if (hasActivePricing && this.currentConfig) {
      // Add a prominent "Start Over" banner to the custom pricing form
      const customPricingForm = document.getElementById('custom-pricing-form');
      if (customPricingForm) {
        const startOverDiv = document.createElement('div');
        startOverDiv.style.cssText = `
          margin: 0 0 1.5rem 0; 
          padding: 1.5rem; 
          background: linear-gradient(135deg, #ff6b35, #f7931e); 
          color: white; 
          border-radius: 8px; 
          text-align: center; 
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          position: relative;
          border: 2px solid rgba(255, 255, 255, 0.2);
        `;
        startOverDiv.innerHTML = `
          <div style="font-size: 1.2rem; font-weight: bold; margin: 0 0 0.8rem 0; display: flex; align-items: center; justify-content: center; gap: 8px;">
            <span style="font-size: 1.4rem;">⚠️</span>
            Active Order in Progress
          </div>
          <p style="margin: 0 0 1rem 0; font-size: 1rem; opacity: 0.95; line-height: 1.4;">
            You have a custom pricing order that hasn't been completed yet.<br>
            <strong>Need to change your pricing options?</strong>
          </p>
          <button type="button" onclick="clearCustomPricing()" style="
            background: rgba(255, 255, 255, 0.9); 
            color: #333; 
            border: none; 
            padding: 12px 24px; 
            border-radius: 6px; 
            cursor: pointer; 
            font-size: 1rem; 
            font-weight: 600;
            transition: all 0.3s ease;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
          " onmouseover="this.style.background='#fff'; this.style.transform='translateY(-1px)';" onmouseout="this.style.background='rgba(255, 255, 255, 0.9)'; this.style.transform='translateY(0)';">
            🔄 Start Over / Clear Previous Order
          </button>
          <div style="margin-top: 1rem; font-size: 0.85rem; opacity: 0.8;">
            This will clear your current order and let you create a new one<br>
           <!-- <a href="/pages/start-over" style="color: rgba(255, 255, 255, 0.9); text-decoration: underline; margin-top: 0.5rem; display: inline-block;">
              → Or use our dedicated Start Over page
            </a> -->
          </div>
        `;
        
        // Insert at the beginning of the form instead of appending
        customPricingForm.insertBefore(startOverDiv, customPricingForm.firstChild);
      }
    }
  }
}

// Check URL for clear parameter on page load
if (window.location.search.includes('clear_custom_pricing=true')) {
  localStorage.removeItem('custom_pricing_active');
  localStorage.removeItem('custom_pricing_checkout_url');
  localStorage.removeItem('last_custom_pricing_product');
  // Remove the parameter from URL
  const url = new URL(window.location);
  url.searchParams.delete('clear_custom_pricing');
  window.history.replaceState({}, document.title, url);
}

// Checkout functionality is handled by checkout-clear-pricing.js

// Global function to clear custom pricing
window.clearCustomPricing = function() {
  const confirmMessage = `Are you sure you want to clear your current custom pricing order?\n\nThis will:\n• Cancel your current order\n• Clear your pricing selections\n• Allow you to create a new order with different options\n\nClick OK to continue or Cancel to keep your current order.`;
  
  if (confirm(confirmMessage)) {
    localStorage.removeItem('custom_pricing_active');
    localStorage.removeItem('custom_pricing_checkout_url');
    localStorage.removeItem('last_custom_pricing_product');
    alert('✅ Previous custom pricing order cleared successfully!\n\nYou can now create a new order with different pricing options.');
    window.location.reload();
  }
};

// Initialize the calculator
window.customPricingCalculator = new CustomPricingCalculator();

// Make calculator available globally
window.getCustomPricing = function() {
  return window.customPricingCalculator.getCurrentPricing();
};

// Add checkout page start-over functionality
(function() {
  'use strict';
  
  // Check if we're on a checkout page
  const isCheckoutPage = window.location.pathname.includes('/checkouts/');
  const hasCustomPricingProduct = isCheckoutPage && (
    (document.body && (document.body.textContent.includes('Alabama Law Weekly') || document.body.textContent.includes('Tennessee Attorneys Memo'))) ||
    (document.documentElement && (document.documentElement.textContent.includes('Alabama Law Weekly') || document.documentElement.textContent.includes('Tennessee Attorneys Memo')))
  );
  
  if (!isCheckoutPage && !hasCustomPricingProduct) {
    return;
  }
  
  console.log('Checkout page detected:', {
    isCheckoutPage,
    pathname: window.location.pathname,
    hasCustomPricingProduct: hasCustomPricingProduct,
    hasCustomPricing: localStorage.getItem('custom_pricing_active')
  });
  
  // Function to add the start over link
  function addStartOverLink() {
    // Don't add if already exists
    if (document.getElementById('custom-pricing-start-over')) {
      return;
    }
    
    console.log('Adding start over link to checkout page');
    
    // Create the start over link
    const startOverDiv = document.createElement('div');
    startOverDiv.id = 'custom-pricing-start-over';
    startOverDiv.style.cssText = `
      position: fixed !important;
      bottom: 20px !important;
      right: 20px !important;
      z-index: 999999 !important;
      background: linear-gradient(135deg, #ff6b35, #f7931e) !important;
      color: white !important;
      padding: 16px 20px !important;
      border-radius: 8px !important;
      font-size: 14px !important;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif !important;
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3) !important;
      border: 2px solid rgba(255, 255, 255, 0.2) !important;
      transition: all 0.3s ease !important;
      cursor: pointer !important;
      max-width: 280px !important;
      text-align: center !important;
    `;
    
    startOverDiv.innerHTML = `
      <div style="font-weight: bold; margin-bottom: 8px; font-size: 15px;">
        🔄 Need Different Pricing?
      </div>
      <div style="font-size: 13px; margin-bottom: 12px; opacity: 0.9; line-height: 1.3;">
        Clear your order and create a new one with different attorney count or rate type
      </div>
      <div style="display: flex; gap: 8px; justify-content: center;">
        <a href="${window.location.origin}/pages/start-over" 
           style="background: rgba(255, 255, 255, 0.9); color: #333; text-decoration: none; padding: 8px 12px; border-radius: 4px; font-weight: 600; font-size: 12px; transition: all 0.2s ease;" 
           onmouseover="this.style.background='#fff'; this.style.transform='scale(1.05)';" 
           onmouseout="this.style.background='rgba(255, 255, 255, 0.9)'; this.style.transform='scale(1)';">
          🔄 Start Over
        </a>
        <button onclick="this.parentElement.parentElement.remove()" 
                style="background: rgba(255, 255, 255, 0.2); color: white; border: 1px solid rgba(255, 255, 255, 0.3); padding: 8px 12px; border-radius: 4px; font-weight: 600; font-size: 12px; cursor: pointer; transition: all 0.2s ease;" 
                onmouseover="this.style.background='rgba(255, 255, 255, 0.3)';" 
                onmouseout="this.style.background='rgba(255, 255, 255, 0.2)';">
          ✕ Close
        </button>
      </div>
    `;
    
    // Add hover effects to the container
    startOverDiv.addEventListener('mouseenter', function() {
      this.style.transform = 'translateY(-2px) scale(1.02)';
      this.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.4)';
    });
    
    startOverDiv.addEventListener('mouseleave', function() {
      this.style.transform = 'translateY(0) scale(1)';
      this.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.3)';
    });
    
    // Add to page - try multiple insertion points
    try {
      document.body.appendChild(startOverDiv);
      console.log('Start over link successfully added to checkout page body');
    } catch (e) {
      try {
        document.documentElement.appendChild(startOverDiv);
        console.log('Start over link added to document element');
      } catch (e2) {
        console.error('Failed to add start over link:', e2);
      }
    }
  }
  
  // Multiple attempts to add the link
  function attemptAddLink() {
    if (document.body || document.documentElement) {
      addStartOverLink();
    }
  }
  
  // Immediate attempt
  attemptAddLink();
  
  // DOM ready attempt
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attemptAddLink);
  } else {
    setTimeout(attemptAddLink, 100);
  }
  
  // Delayed attempts for dynamic content
  setTimeout(attemptAddLink, 500);
  setTimeout(attemptAddLink, 1000);
  setTimeout(attemptAddLink, 2000);
  setTimeout(attemptAddLink, 3000);
  
  // Mutation observer for dynamic changes
  if (window.MutationObserver && (document.body || document.documentElement)) {
    const observer = new MutationObserver(function(mutations) {
      let shouldTryAgain = false;
      mutations.forEach(function(mutation) {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          shouldTryAgain = true;
        }
      });
      
      if (shouldTryAgain) {
        setTimeout(attemptAddLink, 300);
      }
    });
    
    try {
      observer.observe(document.body || document.documentElement, {
        childList: true,
        subtree: true
      });
    } catch (e) {
      console.log('Could not set up mutation observer:', e);
    }
  }
  
})();
